import React, { useCallback, useEffect, useRef } from "react";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useVirtualizer } from "@tanstack/react-virtual";
import SortableCard from "./OptimizedSortableCard";
import type { IColumn, IRoles } from "../../models/LeadData.model";

interface ColumnProps {
  column: IColumn;
  activeId: string | null;
  overColumnId: string | null;
  updateSourceType: (leadId: string, sourceType: string) => void;
  updateAssignee: (
    leadId: string,
    oldAssigneeId: string,
    newAssignee: string,
    index: number,
    columnIndex: string
  ) => void;
  roles: IRoles;
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

const OptimizedKanbanColumn: React.FC<ColumnProps> = ({
  column,
  overColumnId,
  updateSourceType,
  updateAssignee,
  roles,
  isLoading = false,
  onLoadMore,
  hasMore = false,
}) => {
  const { setNodeRef } = useDroppable({
    id: column.id,
    data: { columnId: column.id },
  });

  const parentRef = useRef<HTMLDivElement>(null);
  const isOver = column.id === overColumnId;

  const displayCards = [...column.cards];

  const rowVirtualizer = useVirtualizer({
    count: displayCards.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 130,
    overscan: 10,
  });
  // NEW: Handle infinite scroll
  const handleScroll = useCallback(() => {
    if (!parentRef.current || !onLoadMore || !hasMore || isLoading) return;

    const { scrollTop, scrollHeight, clientHeight } = parentRef.current;
    const threshold = 100; // Load more when 100px from bottom

    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      onLoadMore();
    }
  }, [onLoadMore, hasMore, isLoading]);

  // NEW: Add scroll listener
  useEffect(() => {
    const scrollElement = parentRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll);
      return () => scrollElement.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);
  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col w-80 bg-white border border-gray-200 rounded-xl shadow-md transition-colors duration-150 p-2 ${
        isOver ? "bg-blue-50 border-blue-400" : ""
      }`}
      style={{ height: "calc(70vh - 50px)" }}
    >
      <div className="flex justify-between items-center p-2 mb-4 border-b">
        <h2 className="text-sm font-semibold text-gray-800 truncate">
          {column.name.capitalizeWords()}
        </h2>
      </div>

      <SortableContext
        key={column.id || "idle"}
        items={displayCards.map((c) => `${column.id}-${c._id}`)}
        strategy={verticalListSortingStrategy}
      >
        <div ref={parentRef} className="flex-1 relative overflow-y-auto">
          <div
            className="absolute top-0 left-0 w-full"
            style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const index = virtualRow.index;
              const item = displayCards[index];
              if (!item) return null;
              const key = `${column.id}-${item._id}`;

              return (
                <div
                  key={key}
                  className="absolute inset-x-0 px-2 transition-transform duration-200 ease-in-out"
                  style={{ transform: `translateY(${virtualRow.start}px)` }}
                >
                  <div className="w-full h-auto">
                    <div className="h-full overflow-hidden p-1">
                      <SortableCard
                        card={item}
                        index={index}
                        columnId={column.id}
                        updateSourceType={updateSourceType}
                        updateAssignee={updateAssignee}
                        roles={roles}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {isLoading && (
            <div className="absolute bottom-0 left-0 right-0 p-2 text-center">
              <div className="text-xs text-gray-500">Loading more...</div>
            </div>
          )}
        </div>
      </SortableContext>

      {column.cards.length === 0 && (
        <div className="text-xs text-gray-400 text-center mt-4">
          No cards in this column
        </div>
      )}
    </div>
  );
};

export default OptimizedKanbanColumn;
